import React from "react";
import WeatherWidget from "../WeatherWidget";
import { SearchIcon } from "../icons";
import MapLayerControl, { type BigBlueMapLayerVisibility } from "./MapLayerControl";
import { SEARCH_LURE_FREE_REGEN_CAP } from "../../services/searchLures";

function RecenterTargetIcon({
  className = "",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="6.4" />
      <line x1="12" y1="2.5" x2="12" y2="7" />
      <line x1="12" y1="17" x2="12" y2="21.5" />
      <line x1="2.5" y1="12" x2="7" y2="12" />
      <line x1="17" y1="12" x2="21.5" y2="12" />
      <circle cx="12" cy="12" r="1.95" fill="currentColor" stroke="none" />
    </svg>
  );
}

const BitePlanCompassIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M15.6 8.4l-2.1 5.1-5.1 2.1 2.1-5.1 5.1-2.1z" />
    <path d="M12 3v2" />
    <path d="M12 19v2" />
    <path d="M3 12h2" />
    <path d="M19 12h2" />
  </svg>
);

const CrewDeckIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.15"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M7.2 11.2a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z" />
    <path d="M16.8 11.2a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z" />
    <path d="M3.8 19.2c.35-2.65 1.95-4.3 3.7-4.3s3.35 1.65 3.7 4.3" />
    <path d="M12.8 19.2c.35-2.65 1.95-4.3 3.7-4.3s3.35 1.65 3.7 4.3" />
    <path d="M10.4 13.1c.5-.55 1.05-.82 1.6-.82s1.1.27 1.6.82" />
  </svg>
);

const smallActionButtonStyle =
  "roddo-tactile-hit w-10 h-10 rounded-[13px] flex items-center justify-center overflow-hidden " +
  "bg-[rgba(255,249,239,0.96)] dark:bg-[#101827] " +
  "border border-[#6f573c]/14 dark:border-[rgba(132,151,190,0.18)] " +
  "shadow-[0_4px_12px_rgba(89,65,37,0.09)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.16)] " +
  "transition-[transform,filter,box-shadow] duration-150 ease-out " +
  "hover:-translate-y-[1px] hover:brightness-[1.02] active:translate-y-0 active:scale-[0.97]";

type Props = {
  isDarkMode: boolean;
  headerTopCss: string;
  weatherData: any;
  isLoadingWeather: boolean;
  tempUnit: any;
  locationName?: string | null;
  coords?: any;
  isWeatherOpen: boolean;
  onWeatherExpandedChange: (open: boolean) => void;
  isMapSearchOpen: boolean;
  onOpenMapSearch: () => void;
  mapSearchInputRef: React.RefObject<HTMLInputElement | null>;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  isMapSearchFocused: boolean;
  onMapSearchFocus: () => void;
  onMapSearchBlur: () => void;
  onSearchEscape: () => void;
  onSearchSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onCloseMapSearch: (clear?: boolean) => void;
  onOpenDaily: () => void;
  displayedSearchLureBalance: number;
  compactSearchLureRefillLabel: string;
  onTackleBoxClick?: () => void;
  onRecenter: () => void;
  mapLayerVisibility: BigBlueMapLayerVisibility;
  onToggleMapLayerVisibility: (key: keyof BigBlueMapLayerVisibility) => void;
  onBeforeOpenLayerControl: () => void;
  onOpenCrew: () => void;
  showCrewUnread: boolean;
  crewUnreadCount: number;
  showGlobe: boolean;
  onOpenGlobe: () => void;
  searchError: string | null;
  searchErrorTopCss: string;
  searchLureRefillLabel: string;
};

export default function BigBlueMapHud({
  isDarkMode,
  headerTopCss,
  weatherData,
  isLoadingWeather,
  tempUnit,
  locationName,
  coords,
  isWeatherOpen,
  onWeatherExpandedChange,
  isMapSearchOpen,
  onOpenMapSearch,
  mapSearchInputRef,
  searchQuery,
  onSearchQueryChange,
  isMapSearchFocused,
  onMapSearchFocus,
  onMapSearchBlur,
  onSearchEscape,
  onSearchSubmit,
  onCloseMapSearch,
  onOpenDaily,
  displayedSearchLureBalance,
  compactSearchLureRefillLabel,
  onTackleBoxClick,
  onRecenter,
  mapLayerVisibility,
  onToggleMapLayerVisibility,
  onBeforeOpenLayerControl,
  onOpenCrew,
  showCrewUnread,
  crewUnreadCount,
  showGlobe,
  onOpenGlobe,
  searchError,
  searchErrorTopCss,
  searchLureRefillLabel,
}: Props) {
  return (
    <>
      <div
        className="absolute left-2 right-2 pointer-events-none z-[2005]"
        style={{
          top: headerTopCss,
          ["--roddo-map-hud-top" as any]: headerTopCss,
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="relative shrink-0 h-[220px] pointer-events-auto transition-[width] duration-200 w-[58px]">
            <div className="absolute left-0 top-[56px] w-[56px] h-[56px] flex items-center justify-center">
              <WeatherWidget
                weatherData={weatherData}
                isLoading={isLoadingWeather}
                tempUnit={tempUnit}
                locationName={locationName ?? null}
                coords={coords ?? null}
                isExpanded={isWeatherOpen}
                onExpandedChange={onWeatherExpandedChange}
                panelPlacement="anchor-right"
                isDarkMode={isDarkMode}
              />
            </div>

            {!isMapSearchOpen ? (
              <button
                type="button"
                onClick={onOpenMapSearch}
                className="absolute left-[5px] top-[5px] grid h-[46px] w-[46px] place-items-center rounded-[16px] border backdrop-blur-xl transition-transform active:scale-[0.94]"
                style={{
                  background: isDarkMode ? "rgba(10,18,32,0.92)" : "rgba(255,251,245,0.94)",
                  borderColor: isDarkMode ? "rgba(103,216,232,0.18)" : "rgba(47,128,237,0.15)",
                  color: isDarkMode ? "#BDEFF5" : "#2F80ED",
                  boxShadow: isDarkMode ? "0 5px 14px rgba(0,0,0,0.16)" : "0 7px 18px rgba(89,65,37,0.09)",
                }}
                aria-label="Open map search"
                title="Search places or fish"
              >
                <SearchIcon className="h-[19px] w-[19px]" />
              </button>
            ) : null}

            <button
              type="button"
              data-roddo-quick-anchor="daily"
              aria-label="Open Today’s Bite Plan"
              title="Today’s Bite Plan"
              onClick={onOpenDaily}
              className={[
                "absolute left-[6px] top-[116px] pointer-events-auto",
                "group grid h-11 w-11 place-items-center rounded-[14px]",
                "border shadow-sm transition-all duration-200",
                "active:scale-95 hover:-translate-y-[1px]",
                isDarkMode
                  ? "border-[rgba(132,151,190,0.18)] bg-[#101827] text-[#F2B84B] shadow-black/20"
                  : "border-[#6f573c]/12 bg-[#FFF9EF] text-[#B97810] shadow-[#594125]/10",
              ].join(" ")}
            >
              <BitePlanCompassIcon className="h-[22px] w-[22px]" />
            </button>
          </div>

          <div className="flex-1 min-w-0 flex justify-center pointer-events-auto">
            {isMapSearchOpen ? (
              <div className="min-w-0 w-full max-w-[560px] transition-[width,opacity,transform] duration-200 ease-out">
                <div className="relative w-full">
                  <form onSubmit={onSearchSubmit} className="relative w-full">
                    <input
                      ref={mapSearchInputRef}
                      id="roddo-map-search"
                      type="text"
                      value={searchQuery}
                      onChange={(event) => onSearchQueryChange(event.target.value)}
                      onFocus={onMapSearchFocus}
                      onBlur={onMapSearchBlur}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          event.preventDefault();
                          onSearchEscape();
                        }
                      }}
                      placeholder="Search places or fish..."
                      className="w-full rounded-[18px] border py-[11px] pl-4 pr-[76px] backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/20 dark:focus:ring-[#67D8E8]/28"
                      style={{
                        background: isDarkMode
                          ? "rgba(12,20,35,0.985)"
                          : "linear-gradient(180deg, rgba(255,250,242,0.98), rgba(248,239,229,0.98))",
                        color: isDarkMode ? "#F2F6FC" : "#2E3A49",
                        borderColor: isDarkMode ? "rgba(132,151,190,0.18)" : "rgba(111,87,60,0.14)",
                        boxShadow: isDarkMode
                          ? "0 5px 14px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.03)"
                          : "0 8px 22px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.78)",
                      }}
                    />
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onCloseMapSearch(true)}
                      className="absolute right-[42px] top-1/2 grid h-[28px] w-[28px] -translate-y-1/2 place-items-center rounded-full text-[16px] font-black opacity-70 transition hover:opacity-100 active:scale-[0.95]"
                      aria-label="Close map search"
                      title="Close search"
                      style={{ color: isDarkMode ? "#B7C2D3" : "#766C62" }}
                    >
                      ×
                    </button>
                    <button
                      type="submit"
                      className="absolute right-[8px] top-1/2 flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-full transition hover:opacity-100 active:scale-[0.96]"
                      style={{
                        background: isDarkMode ? "rgba(103,216,232,0.08)" : "rgba(47,128,237,0.07)",
                        border: isDarkMode
                          ? "1px solid rgba(132,151,190,0.14)"
                          : "1px solid rgba(47,128,237,0.10)",
                      }}
                      aria-label="Search map"
                      title="Search"
                    >
                      <SearchIcon className={isDarkMode ? "h-3.5 w-3.5 text-[#BDEFF5]" : "h-3.5 w-3.5 text-[#2F80ED]"} />
                    </button>
                  </form>

                  {isMapSearchFocused && !searchQuery.trim() ? (
                    <div
                      className="mx-3 mt-2 rounded-2xl border px-3 py-2 text-[11px] font-bold shadow-sm backdrop-blur-xl"
                      style={{
                        background: isDarkMode ? "rgba(16,24,39,0.96)" : "rgba(255,252,246,0.90)",
                        borderColor: isDarkMode ? "rgba(132,151,190,0.14)" : "rgba(91,67,49,0.10)",
                        color: isDarkMode ? "rgba(226,219,245,0.82)" : "#6f6559",
                      }}
                    >
                      Try “bass”, “carp”, “trout”, or a lake / city name.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="shrink-0 flex flex-col items-end justify-start gap-2 pointer-events-auto">
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={onTackleBoxClick}
                className="flex min-h-[46px] min-w-[46px] flex-col items-center justify-center rounded-[14px] border px-1.5 py-1 text-center shadow-sm backdrop-blur-xl transition-transform active:scale-[0.94]"
                style={{
                  background: isDarkMode ? "rgba(10,18,32,0.92)" : "rgba(255,251,245,0.94)",
                  borderColor: isDarkMode ? "rgba(103,216,232,0.18)" : "rgba(47,128,237,0.15)",
                  color: isDarkMode ? "#BDEFF5" : "#28698B",
                  boxShadow: isDarkMode ? "0 5px 14px rgba(0,0,0,0.16)" : "0 7px 18px rgba(89,65,37,0.09)",
                }}
                aria-label={`Open Search Lures shop. ${displayedSearchLureBalance} Search Lures available${compactSearchLureRefillLabel ? `, next free lure in ${compactSearchLureRefillLabel}` : ""}`}
                title="Get more Search Lures"
              >
                <div className="flex items-center gap-0.5 text-[10px] font-black leading-none">
                  <span aria-hidden="true">🪝</span>
                  <span>{displayedSearchLureBalance}</span>
                </div>
                {displayedSearchLureBalance < SEARCH_LURE_FREE_REGEN_CAP && compactSearchLureRefillLabel ? (
                  <div className="mt-0.5 whitespace-nowrap text-[8px] font-bold leading-none opacity-70">
                    {compactSearchLureRefillLabel}
                  </div>
                ) : null}
              </button>

              <button onClick={onRecenter} className={smallActionButtonStyle} aria-label="Recenter" type="button">
                <RecenterTargetIcon
                  className="w-[22px] h-[22px]"
                  style={{
                    color: isDarkMode ? "#BDEFF5" : "#2F80ED",
                    filter: isDarkMode
                      ? "drop-shadow(0 2px 5px rgba(103,216,232,0.12))"
                      : "drop-shadow(0 3px 6px rgba(79,70,229,0.12))",
                  }}
                />
              </button>
            </div>

            <MapLayerControl
              isDarkMode={isDarkMode}
              visibility={mapLayerVisibility}
              buttonClassName={smallActionButtonStyle}
              onToggle={onToggleMapLayerVisibility}
              onBeforeOpen={onBeforeOpenLayerControl}
            />

            <button
              type="button"
              data-roddo-tour="map-crew"
              data-roddo-quick-anchor="crew"
              aria-label="Open Crew Deck"
              title="Crew Deck"
              onClick={onOpenCrew}
              className={[
                smallActionButtonStyle,
                "roddo-hud-attention relative overflow-visible text-[#24799B] dark:text-[#9BDDF4]",
              ].join(" ")}
              style={{
                "--roddo-attention-ring": isDarkMode ? "rgba(102,217,232,0.58)" : "rgba(36,121,155,0.46)",
                "--roddo-attention-glow": isDarkMode ? "rgba(102,217,232,0.42)" : "rgba(36,121,155,0.28)",
                "--roddo-attention-soft": isDarkMode ? "rgba(103,216,232,0.14)" : "rgba(88,177,207,0.18)",
                "--roddo-attention-delay": "0.15s",
              } as React.CSSProperties}
            >
              <span className="roddo-hud-attention-halo" aria-hidden="true" />
              <span className="roddo-hud-attention-icon">
                <CrewDeckIcon className="h-[22px] w-[22px]" />
              </span>

              {showCrewUnread && crewUnreadCount > 0 ? (
                <span
                  className="pointer-events-none absolute -right-1.5 -top-1.5 z-30 grid h-[18px] min-w-[18px] place-items-center rounded-full border px-1 text-[9px] font-black leading-none text-white shadow-[0_2px_6px_rgba(0,0,0,0.24)]"
                  style={{
                    background: isDarkMode ? "#8A70F2" : "#2F80ED",
                    borderColor: isDarkMode ? "#0A1120" : "#FFF9F1",
                  }}
                  aria-label={`${crewUnreadCount} unread Crew message${crewUnreadCount === 1 ? "" : "s"}`}
                >
                  {crewUnreadCount > 99 ? "99+" : crewUnreadCount}
                </span>
              ) : null}
            </button>

            {showGlobe ? (
              <button
                type="button"
                aria-label="Open Big Blue Globe"
                title="Big Blue Globe"
                onClick={onOpenGlobe}
                className={[smallActionButtonStyle, "text-[#2f68ff] dark:text-[#9FE7FF]"].join(" ")}
              >
                <span className="text-[22px] leading-none" aria-hidden="true">🌐</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {searchError ? (
        <div
          className={[
            "absolute left-1/2 -translate-x-1/2 z-[1001] transition-opacity duration-200",
            /no search lures/i.test(searchError) && onTackleBoxClick ? "pointer-events-auto" : "pointer-events-none",
          ].join(" ")}
          style={{ top: searchErrorTopCss }}
          aria-live="polite"
        >
          <div className="max-w-[320px] rounded-2xl border border-white/15 bg-black/70 px-3 py-2.5 text-center text-[12px] leading-snug text-white shadow-sm backdrop-blur-md">
            <div>{searchError}</div>
            {/no search lures/i.test(searchError) ? (
              <div className="mt-1.5 text-[10px] font-bold text-white/70">{searchLureRefillLabel}</div>
            ) : null}
            {/no search lures/i.test(searchError) && onTackleBoxClick ? (
              <button
                type="button"
                onClick={onTackleBoxClick}
                className="mt-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.08em] text-white transition active:scale-[0.98]"
              >
                Get more in The Hold
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
